import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { KebabMenu } from "@/components/KebabMenu";
import { Modal, ModalTitle } from "@/components/Modal";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { TextInput } from "@/components/TextInput";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { LoadError } from "@/components/LoadError";
import { EmptyState } from "@/components/EmptyState";
import { hasRole } from "@/lib/hasRole";
import { MemberStack } from "./teams/MemberStack";
import { MemberTeams } from "./teams/MemberTeams";
import type { Team } from "./teams/shared";

interface OrgUser {
  id: string;
  name: string;
  email: string;
}

const COLUMNS = ["Team", "Members", "Created by", "Created"] as const;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3.5 w-40 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-56 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-6 w-20 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 shrink-0 rounded-full bg-surface" />
              <div className="h-3 w-28 rounded bg-surface" />
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td />
        </tr>
      ))}
    </>
  );
}

export function Teams() {
  const { user } = useAuth();

  return hasRole(user?.roles, "Admin") ? <AdminTeams /> : <MemberTeams />;
}

function AdminTeams() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const hasFilters = Boolean(debouncedNameFilter || createdByFilter);

  const fetchTeams = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    if (debouncedNameFilter) {
      params.set("name", debouncedNameFilter);
    }

    if (createdByFilter) {
      params.set("createdByUserId", createdByFilter);
    }

    return api.get<PagedResult<Team>>(
      `/organizations/${orgId}/teams?${params.toString()}`,
    );
  }, [accessToken, api, orgId, page, debouncedNameFilter, createdByFilter]);

  const applyTeams = useCallback((data: PagedResult<Team> | null) => {
    if (data) {
      setTeams(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } else {
      setError("Couldn't load teams.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchTeams().then((data) => {
      if (!cancelled) {
        applyTeams(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTeams, applyTeams]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<PagedResult<OrgUser>>(
        `/organizations/${orgId}/users?page=1&pageSize=100`,
      )
      .then((data) => {
        if (!cancelled) {
          setOrgUsers(data?.items ?? []);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  function retry() {
    setError(null);
    setTeams(null);
    void fetchTeams().then(applyTeams);
  }

  function clearFilters() {
    setNameFilter("");
    setCreatedByFilter("");
    setPage(1);
  }

  async function handleCreateTeam(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    setCreating(true);

    try {
      await api.send(
        `/organizations/${orgId}/teams`,
        "POST",
        { name: newTeamName, description: newTeamDescription || null },
        "Couldn't create team.",
      );

      showToast(`${newTeamName} created.`, "success");
      setNewTeamName("");
      setNewTeamDescription("");
      setShowCreateModal(false);
      applyTeams(await fetchTeams());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't create team.",
        "error",
      );
    } finally {
      setCreating(false);
    }
  }

  const isLoading = teams === null && !error;
  const isEmpty = teams !== null && totalCount === 0;

  const createButton = (
    <Button variant="primary" onClick={() => setShowCreateModal(true)}>
      Create team
    </Button>
  );

  return (
    <div className="text-foreground">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Teams</h1>
          {teams !== null && (
            <p className="mt-0.5 text-sm text-foreground/70">
              {plural(totalCount, "team")}
            </p>
          )}
        </div>
        {createButton}
      </div>

      <div className="mb-4 flex gap-2">
        <SearchField
          value={nameFilter}
          onChange={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
          label="Search teams by name"
          placeholder="Search team name"
        />
        <Select
          aria-label="Filter by creator"
          value={createdByFilter}
          onChange={(e) => {
            setCreatedByFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All creators</option>
          {orgUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
      </div>

      {hasFilters && teams !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(totalCount, "team")}</span>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)} scrollable={false}>
          <ModalTitle className="mb-4 shrink-0">Create a team</ModalTitle>
          <form
            onSubmit={handleCreateTeam}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <div>
                <label
                  htmlFor="teamName"
                  className="mb-1.5 block text-sm text-foreground"
                >
                  Name
                </label>
                <TextInput
                  type="text"
                  id="teamName"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  required
                  autoFocus
                  className="w-full"
                />
              </div>
              <div>
                <label
                  htmlFor="teamDescription"
                  className="mb-1.5 block text-sm text-foreground"
                >
                  Description{" "}
                  <span className="text-foreground/70">(optional)</span>
                </label>
                <textarea
                  id="teamDescription"
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-muted bg-surface px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                />
              </div>
            </div>
            <div className="mt-4 flex shrink-0 justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={creating}>
                {creating ? "Creating..." : "Create team"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {error && <LoadError what="teams" onRetry={retry} className="mb-4" />}

      {isEmpty ? (
        <EmptyState
          title={hasFilters ? "No teams match these filters" : "No teams yet"}
          description={
            hasFilters
              ? "Try a different name or creator."
              : "Teams let you assign resource access to multiple people at once."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
                <th scope="col" className="border-b border-surface pb-2.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                teams?.map((team) => {
                  return (
                    <tr
                      key={team.id}
                      className="group h-14 border-b border-surface/70 hover:bg-surface"
                    >
                      <td className="py-2 pr-4">
                        <div className="text-foreground">{team.name}</div>
                        {team.description && (
                          <div className="truncate text-xs text-foreground/70">
                            {team.description}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <MemberStack members={team.members} />
                      </td>
                      <td className="py-2 pr-4">
                        {team.createdBy ? (
                          <div className="flex items-center gap-2">
                            <Avatar
                              name={team.createdBy.name}
                              email={team.createdBy.email}
                              avatarId={team.createdBy.avatarId}
                              size="md"
                            />
                            <span className="text-foreground/70">
                              {team.createdBy.name}
                            </span>
                          </div>
                        ) : (
                          <Chip>Created by SCIM</Chip>
                        )}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-foreground/70">
                        {formatDate(team.createdAt)}
                      </td>
                      <td className="py-2 text-right">
                        <KebabMenu
                          label={`More actions for ${team.name}`}
                          items={[
                            {
                              label: "Members",
                              onClick: () =>
                                navigate(`/${orgId}/teams/${team.id}/members`),
                            },
                            {
                              label: "Access rules",
                              onClick: () =>
                                navigate(
                                  `/${orgId}/teams/${team.id}/access-rules`,
                                ),
                            },
                            {
                              label: "Settings",
                              onClick: () =>
                                navigate(`/${orgId}/teams/${team.id}/settings`),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={Math.ceil(totalCount / PAGE_SIZE)}
        onPageChange={setPage}
      />

      <Toast toast={toast} />
    </div>
  );
}
